import {
  CommentDiscussionIcon,
  GitBranchIcon,
  GitCommitIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueOpenedIcon,
  LinkExternalIcon,
  PersonIcon,
  RepoIcon,
  TagIcon,
  type Icon,
} from '@primer/octicons-react'
import {BranchName, VisuallyHidden} from '@primer/react'
import {createContext} from 'react'

import {ReferenceCard} from '@github-ui/agent-sessions-interface/components/ReferenceCard'
import {ReferenceList} from '@github-ui/agent-sessions-interface/components/ReferenceList'
import styles from './GitHubReferenceCards.module.css'
import type {BranchReference, GithubReferenceState, ReferenceItem} from './github-references'

export type GitHubReferenceClickHandler = (ref: ReferenceItem, event: React.MouseEvent) => void

export const GitHubReferenceClickContext = createContext<GitHubReferenceClickHandler | undefined>(undefined)

const REFERENCE_TYPE_LABEL: Record<ReferenceItem['type'], string> = {
  pr: 'Pull request',
  issue: 'Issue',
  commit: 'Commit',
  repo: 'Repository',
  discussion: 'Discussion',
  release: 'Release',
  branch: 'Branch',
  external: 'External resource',
  user: 'User',
}

/** Resolve the leading octicon for a reference, refining PR/issue by state. */
function referenceIcon(type: ReferenceItem['type'], state?: GithubReferenceState): Icon {
  switch (type) {
    case 'pr':
      if (state === 'merged') return GitMergeIcon
      if (state === 'closed') return GitPullRequestClosedIcon
      if (state === 'draft') return GitPullRequestDraftIcon
      return GitPullRequestIcon
    case 'issue':
      return state === 'closed' ? IssueClosedIcon : IssueOpenedIcon
    case 'commit':
      return GitCommitIcon
    case 'discussion':
      return CommentDiscussionIcon
    case 'release':
      return TagIcon
    case 'repo':
      return RepoIcon
    case 'branch':
      return GitBranchIcon
    case 'external':
      return LinkExternalIcon
    case 'user':
      return PersonIcon
  }
}

function referenceIconClassName(reference: ReferenceItem): string | undefined {
  if (reference.type === 'branch') return styles.iconAccent
  const state = 'state' in reference ? reference.state : undefined
  switch (state) {
    case 'open':
      return styles.iconOpen
    case 'closed':
      return styles.iconClosed
    case 'merged':
      return styles.iconMerged
    case 'draft':
    case undefined:
      return styles.iconMuted
  }
}

function referenceTitle(reference: ReferenceItem): React.ReactNode {
  if (reference.type === 'branch') {
    return <span aria-hidden="true">{reference.headBranch}</span>
  }

  const title = reference.title
  const number = 'number' in reference ? reference.number : undefined
  return (
    <>
      {title}
      {title && number != null ? ' ' : null}
      {number != null && <span className={styles.number}>#{number}</span>}
    </>
  )
}

function branchMetadata(reference: BranchReference): React.ReactNode {
  return (
    <span className={styles.branchComparison} aria-hidden="true">
      <BranchName as="span" className={styles.branchName}>
        {reference.baseBranch}
      </BranchName>
      <span aria-hidden="true">←</span>
      <BranchName as="span" className={styles.branchName}>
        {reference.headBranch}
      </BranchName>
    </span>
  )
}

function referenceMetadata(reference: ReferenceItem): React.ReactNode {
  if (reference.type === 'branch') return branchMetadata(reference)
  if (reference.type === 'external' || reference.type === 'user') return reference.metadata
  return reference.repo
}

function referenceAccessiblePrefix(reference: ReferenceItem): string {
  if (reference.type === 'branch') {
    return `Branch: ${reference.headBranch}. Base branch: ${reference.baseBranch}; head branch: ${reference.headBranch}.`
  }
  const type = REFERENCE_TYPE_LABEL[reference.type]
  const state = 'state' in reference ? reference.state : undefined
  return state ? `${type}, ${state}: ` : `${type}: `
}

interface GitHubReferenceCardsProps<T extends ReferenceItem> {
  items: T[]
  className?: string
  'aria-label'?: string
  onItemClick?: (item: T, event: React.MouseEvent<HTMLAnchorElement>) => void
}

/**
 * Render GitHub references as a full-width, panel-style list. This adapter resolves each
 * reference's icon, title, number, and metadata, then delegates all
 * layout/markup to the generic {@link ReferenceList} / {@link ReferenceCard}.
 * Callers pass already-normalized references (see {@link normalizeGithubReferences}).
 */
export function GitHubReferenceCards<T extends ReferenceItem>({
  items,
  className,
  'aria-label': ariaLabel = 'GitHub references',
  onItemClick,
}: GitHubReferenceCardsProps<T>) {
  if (items.length === 0) return null
  return (
    <ReferenceList className={className} aria-label={ariaLabel}>
      {items.map((ref, index) => {
        const state = 'state' in ref ? ref.state : undefined
        const CardIcon = referenceIcon(ref.type, state)
        const metadata = referenceMetadata(ref)
        const opensNewTab = ref.type === 'external'
        return (
          <ReferenceCard
            // eslint-disable-next-line @eslint-react/no-array-index-key -- references are a static, order-stable list with no stable id
            key={index}
            href={ref.url}
            target={opensNewTab ? '_blank' : undefined}
            leadingVisual={<CardIcon className={referenceIconClassName(ref)} />}
            title={
              <>
                <VisuallyHidden>{referenceAccessiblePrefix(ref)}</VisuallyHidden>
                {referenceTitle(ref)}
              </>
            }
            metadata={metadata}
            onClick={onItemClick ? (event: React.MouseEvent<HTMLAnchorElement>) => onItemClick(ref, event) : undefined}
          />
        )
      })}
    </ReferenceList>
  )
}
